import { formatMoney } from '@theme/money-formatting';

const STORAGE_KEY = 'twosix:wishlist:v1';
const CHANGE_EVENT = 'wishlist:change';
const rootUrl = window.Shopify?.routes?.root ?? '/';

const hasLocalStorageSupport = (() => {
  try {
    const testKey = '__twosix_wishlist__';
    window.localStorage.setItem(testKey, '1');
    window.localStorage.removeItem(testKey);
    return true;
  } catch (error) {
    console.warn('Wishlist localStorage unavailable, falling back to in-memory store.');
    return false;
  }
})();

/**
 * Parses JSON safely and enforces the wishlist entry format
 * @param {string|null} rawValue - Raw JSON string from storage
 * @returns {{handle: string, variantId?: string | null, productId?: string | null}[]}
 */
function parseWishlist(rawValue) {
  if (!rawValue) return [];
  try {
    const parsed = JSON.parse(rawValue);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .map((entry) => ({
        handle: typeof entry?.handle === 'string' ? entry.handle : null,
        variantId: entry?.variantId ?? null,
        productId: entry?.productId ?? null,
      }))
      .filter((entry) => entry.handle);
  } catch (error) {
    console.warn('Unable to parse wishlist storage', error);
    return [];
  }
}

class WishlistStore {
  /**
   * @param {string} key
   */
  constructor(key = STORAGE_KEY) {
    this.storageKey = key;
    this.entries = parseWishlist(this.#readFromStorage());
  }

  /**
   * Reads wishlist from storage
   * @returns {string | null}
   */
  #readFromStorage() {
    if (!hasLocalStorageSupport) return null;
    try {
      return window.localStorage.getItem(this.storageKey);
    } catch (error) {
      console.warn('Unable to read wishlist storage', error);
      return null;
    }
  }

  /**
   * Persists wishlist to storage
   */
  #writeToStorage() {
    if (!hasLocalStorageSupport) {
      this.#emit();
      return;
    }

    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(this.entries));
    } catch (error) {
      console.warn('Unable to save wishlist storage', error);
    }

    this.#emit();
  }

  /**
   * Dispatches change event
   */
  #emit() {
    document.dispatchEvent(
      new CustomEvent(CHANGE_EVENT, {
        detail: {
          items: this.getItems(),
        },
      })
    );
  }

  /**
   * Returns immutable list of wishlist items
   * @returns {{handle: string, variantId?: string | null, productId?: string | null}[]}
   */
  getItems() {
    return [...this.entries];
  }

  /**
   * Checks if a handle is already saved
   * @param {string} handle
   * @returns {boolean}
   */
  has(handle) {
    if (!handle) return false;
    return this.entries.some((entry) => entry.handle === handle);
  }

  /**
   * Adds a wishlist entry
   * @param {{handle: string, variantId?: string | null, productId?: string | null}} entry
   */
  add(entry) {
    if (!entry?.handle || this.has(entry.handle)) return;
    this.entries = [{
      handle: entry.handle,
      variantId: entry.variantId ?? null,
      productId: entry.productId ?? null,
    }, ...this.entries];
    this.#writeToStorage();
  }

  /**
   * Removes a wishlist entry for a given handle
   * @param {string} handle
   */
  remove(handle) {
    if (!handle) return;
    this.entries = this.entries.filter((entry) => entry.handle !== handle);
    this.#writeToStorage();
  }

  /**
   * Toggles a wishlist entry
   * @param {{handle: string, variantId?: string | null, productId?: string | null}} entry
   */
  toggle(entry) {
    if (!entry?.handle) return;
    if (this.has(entry.handle)) {
      this.remove(entry.handle);
    } else {
      this.add(entry);
    }
  }
}

const wishlistStore = new WishlistStore();

const htmlEscape = (value) =>
  typeof value === 'string'
    ? value.replace(/[&<>"']/g, (character) => {
        const entities = {
          '&': '&amp;',
          '<': '&lt;',
          '>': '&gt;',
          '"': '&quot;',
          "'": '&#39;',
        };
        return entities[character] || character;
      })
    : '';

/**
 * Updates any wishlist count badges when state changes
 */
function updateCountBadges() {
  const count = wishlistStore.getItems().length;
  document.querySelectorAll('[data-wishlist-count]').forEach((element) => {
    element.textContent = String(count);
    if (element.dataset.hideWhenEmpty === 'true') {
      element.toggleAttribute('hidden', count === 0);
    }
  });
}

document.addEventListener(CHANGE_EVENT, updateCountBadges);
updateCountBadges();

class WishlistToggleElement extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('button');
    if (!this.button || !this.dataset.productHandle) return;
    this.abortController = new AbortController();
    this.button.addEventListener('click', this.#handleClick, {
      signal: this.abortController.signal,
    });
    document.addEventListener(CHANGE_EVENT, this.#syncState, {
      signal: this.abortController.signal,
    });
    this.#syncState();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  get handle() {
    return this.dataset.productHandle;
  }

  #handleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    wishlistStore.toggle({
      handle: this.handle,
      variantId: this.dataset.variantId || null,
      productId: this.dataset.productId || null,
    });
  };

  #syncState = () => {
    const saved = wishlistStore.has(this.handle);
    const addLabel = this.dataset.labelAdd || 'Add to wishlist';
    const removeLabel = this.dataset.labelRemove || 'Remove from wishlist';
    const savedLabel = this.dataset.labelAdded || removeLabel;
    const textTarget = this.querySelector('[data-wishlist-label]');
    const srLabel = this.querySelector('[data-wishlist-sr-label]');

    if (this.button) {
      this.button.classList.toggle('is-active', saved);
      this.button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      this.button.setAttribute('aria-label', saved ? removeLabel : addLabel);
    }

    if (textTarget) {
      textTarget.textContent = saved ? savedLabel : addLabel;
    }

    if (srLabel) {
      srLabel.textContent = saved ? removeLabel : addLabel;
    }
  };
}

if (!customElements.get('wishlist-toggle')) {
  customElements.define('wishlist-toggle', WishlistToggleElement);
}

/**
 * Renders wishlist page grid
 */
class WishlistPageElement extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-wishlist-grid]');
    this.emptyState = this.querySelector('[data-wishlist-empty]');
    this.loadingState = this.querySelector('[data-wishlist-loading]');
    this.errorState = this.querySelector('[data-wishlist-error]');
    this.currency = this.dataset.currency || window.Shopify?.currency?.active || window.Shopify?.currency || 'USD';
    this.moneyFormat = this.dataset.moneyFormat || window.theme?.moneyFormat || '{{amount}}';
    this.addLabel = this.dataset.addLabel || 'Add to wishlist';
    this.removeLabel = this.dataset.removeLabel || 'Remove from wishlist';
    this.savedLabel = this.dataset.savedLabel || this.removeLabel;
    this.soldOutLabel = this.dataset.soldOutLabel || 'Sold out';
    this.loadingText = this.dataset.loadingText || '';
    this.errorMessage = this.dataset.errorMessage || '';
    this.placeholderCount = Number(this.dataset.placeholderCount || '3');
    this.quickAddEnabled = this.dataset.quickAddEnabled === 'true';
    this.quickAddDesktop = this.dataset.quickAddDesktop === 'true';
    this.quickAddMobile = this.dataset.quickAddMobile === 'true';
    this.quickAddColorClass = this.dataset.quickAddColor || '';
    this.quickAddIcon = this.dataset.quickAddIcon || '';
    this.quickAddChooseText = this.dataset.quickAddChooseText || 'Choose options';

    this.designMode = Boolean(window.Shopify?.designMode);
    this.abortController = new AbortController();

    document.addEventListener(CHANGE_EVENT, this.#handleWishlistChange, {
      signal: this.abortController.signal,
    });

    this.render();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  #handleWishlistChange = () => {
    this.render();
  };

  async render() {
    const items = wishlistStore.getItems();

    if (!items.length) {
      if (this.designMode) {
        this.#renderPlaceholders();
      } else {
        this.#showEmpty();
      }
      return;
    }

    this.#showLoading();

    try {
      const productMap = await this.#fetchProducts(items);
      if (!productMap.size) {
        this.#showEmpty();
        return;
      }
      this.#renderProducts(items, productMap);
    } catch (error) {
      console.error('Wishlist failed to load products', error);
      this.#showError();
    }
  }

  /**
   * Fetches product JSON objects for given handles
   * @param {{handle: string}[]} items
   * @returns {Promise<Map<string, import('@shopify/theme-product').Product>>}
   */
  async #fetchProducts(items) {
    const handles = [...new Set(items.map((item) => item.handle).filter(Boolean))];
    const requests = handles.map(async (handle) => {
      try {
        const response = await fetch(`${rootUrl}products/${encodeURIComponent(handle)}.js`);
        if (!response.ok) return null;
        const product = await response.json();
        return product?.handle ? product : null;
      } catch (error) {
        console.warn(`Unable to fetch product ${handle}`, error);
        return null;
      }
    });

    const results = await Promise.all(requests);
    const map = new Map();
    results.forEach((product) => {
      if (product?.handle) map.set(product.handle, product);
    });
    return map;
  }

  #renderProducts(items, productMap) {
    if (!this.grid) return;
    const fragment = document.createDocumentFragment();

    items.forEach((item) => {
      const product = productMap.get(item.handle);
      if (!product) return;
      fragment.appendChild(this.#createCard(product));
    });

    if (!fragment.childNodes.length) {
      this.#showEmpty();
      return;
    }

    this.grid.replaceChildren(fragment);
    this.grid.removeAttribute('hidden');
    this.emptyState?.setAttribute('hidden', '');
    this.loadingState?.setAttribute('hidden', '');
    this.errorState?.setAttribute('hidden', '');
  }

  #createCard(product) {
    const article = document.createElement('article');
    article.className = 'wishlist-card';
    article.dataset.productHandle = product.handle;

    const image = product?.featured_image || product?.images?.[0] || null;
    const imageMarkup = image
      ? `<img src="${htmlEscape(image)}" loading="lazy" alt="${htmlEscape(product.title)}" />`
      : '<div class="wishlist-card__placeholder" aria-hidden="true"></div>';

    const minPrice = typeof product.price_min === 'number' ? product.price_min : product.price;
    const maxPrice = typeof product.price_max === 'number' ? product.price_max : minPrice;
    const compareMin = typeof product.compare_at_price_min === 'number' ? product.compare_at_price_min : product.compare_at_price;
    const priceMarkup = this.#formatPrice(minPrice, maxPrice);
    const compareMarkup = compareMin && compareMin > minPrice ? this.#formatPrice(product.compare_at_price_min, product.compare_at_price_max ?? compareMin) : '';

    const soldOut = product.available === false;
    const toggleMarkup = this.#createWishlistToggle(product);
    const quickAddMarkup = this.quickAddEnabled ? this.#createQuickAdd(product) : '';

    article.innerHTML = `
      <div class="wishlist-card__media">
        <a class="wishlist-card__media-link" href="${htmlEscape(product.url)}" aria-label="${htmlEscape(product.title)}">
          ${imageMarkup}
        </a>
        ${toggleMarkup}
        ${quickAddMarkup}
      </div>
      <div class="wishlist-card__content">
        <div class="wishlist-card__info">
          <a class="wishlist-card__title" href="${htmlEscape(product.url)}">${htmlEscape(product.title)}</a>
          <div class="wishlist-card__pricing">
            <span class="wishlist-card__price">${htmlEscape(priceMarkup)}</span>
            ${compareMarkup ? `<span class="wishlist-card__compare">${htmlEscape(compareMarkup)}</span>` : ''}
          </div>
            ${soldOut ? `<p class="wishlist-card__badge" aria-live="polite">${htmlEscape(this.soldOutLabel)}</p>` : ''}
        </div>
      </div>
    `;

    if (this.quickAddEnabled) {
      article.style.setProperty('--quick-add-display', this.quickAddDesktop ? 'flex' : 'none');
      article.style.setProperty('--quick-add-mobile-display', this.quickAddMobile ? 'flex' : 'none');
      article.style.setProperty('--quick-add-mobile-opacity', this.quickAddMobile ? '1' : '0');
    }

    return article;
  }

  #createWishlistToggle(product) {
    if (!product?.handle) return '';
    const addLabel = htmlEscape(this.addLabel);
    const removeLabel = htmlEscape(this.removeLabel);
    const savedLabel = htmlEscape(this.savedLabel);
    const attributes = [
      'class="wishlist-toggle"',
      `data-product-handle="${htmlEscape(product.handle)}"`,
    ];

    if (product?.id) {
      attributes.push(`data-product-id="${htmlEscape(String(product.id))}"`);
    }

    const variantId = product?.variants?.[0]?.id;
    if (variantId) {
      attributes.push(`data-variant-id="${htmlEscape(String(variantId))}"`);
    }

    attributes.push(
      `data-label-add="${addLabel}"`,
      `data-label-remove="${removeLabel}"`,
      `data-label-added="${savedLabel}"`,
      'data-appearance="icon"'
    );

    return `
      <div class="wishlist-card__wishlist product-card__wishlist">
        <wishlist-toggle ${attributes.join(' ')}>
          <button
            type="button"
            class="wishlist-button wishlist-button--icon"
            aria-pressed="false"
            aria-label="${addLabel}"
          >
            <svg
              class="wishlist-button__icon-shape"
              role="presentation"
              focusable="false"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 18.109 27.084"
              aria-hidden="true"
            >
              <path
                d="M0,3.333A3.333,3.333,0,0,1,3.333,0H14.776a3.333,3.333,0,0,1,3.333,3.333V25.895a1.111,1.111,0,0,1-1.839.839l-4.861-4.219A3.333,3.333,0,0,0,7.1,22.462L1.819,26.827A1.111,1.111,0,0,1,0,25.971Z"
              />
            </svg>
            <span class="visually-hidden" data-wishlist-sr-label>${addLabel}</span>
          </button>
        </wishlist-toggle>
      </div>
    `;
  }

  #createQuickAdd(product) {
    if (!product?.url) return '';
    const classes = ['quick-add'];
    if (this.quickAddColorClass) classes.push(this.quickAddColorClass);
    const optionsCount = Array.isArray(product?.options) ? product.options.length : 0;
    const selectedVariantId = this.#getPreferredVariantId(product);
    const iconMarkup = this.quickAddIcon;
    const chooseText = htmlEscape(this.quickAddChooseText);
    const productTitle = htmlEscape(product.title);
    const productUrl = htmlEscape(product.url);

    return `
      <quick-add-component
        class="${classes.join(' ')}"
        data-product-title="${productTitle}"
        data-product-url="${productUrl}"
        data-product-options-count="${optionsCount}"
        data-selected-variant-id="${selectedVariantId}"
        data-quick-add-button="choose"
      >
        <button
          class="button quick-add__button quick-add__button--choose add-to-cart-button"
          type="button"
          name="add"
          on:click="quick-add-component/handleClick"
        >
          <span class="add-to-cart-text">
            <span aria-hidden="true" class="svg-wrapper add-to-cart-icon">${iconMarkup}</span>
            <span class="add-to-cart-text__content is-visually-hidden-mobile">
              <span><span>${chooseText}</span></span>
            </span>
          </span>
        </button>
      </quick-add-component>
    `;
  }

  #getPreferredVariantId(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    const availableVariant = variants.find((variant) => variant?.available);
    const variant = availableVariant || variants[0];
    return variant?.id ? htmlEscape(String(variant.id)) : '';
  }

  #formatPrice(minPrice, maxPrice) {
    if (typeof minPrice !== 'number') return '';
    const firstPrice = formatMoney(minPrice, this.moneyFormat, this.currency);
    if (!maxPrice || minPrice === maxPrice) return firstPrice;
    return `${firstPrice} – ${formatMoney(maxPrice, this.moneyFormat, this.currency)}`;
  }

  #showEmpty() {
    this.grid?.setAttribute('hidden', '');
    this.loadingState?.setAttribute('hidden', '');
    this.errorState?.setAttribute('hidden', '');
    this.emptyState?.removeAttribute('hidden');
  }

  #showLoading() {
    this.loadingState?.removeAttribute('hidden');
    this.grid?.setAttribute('hidden', '');
    this.emptyState?.setAttribute('hidden', '');
    this.errorState?.setAttribute('hidden', '');
  }

  #showError() {
    this.grid?.setAttribute('hidden', '');
    this.emptyState?.setAttribute('hidden', '');
    this.loadingState?.setAttribute('hidden', '');
    if (this.errorState) {
      if (this.errorMessage) this.errorState.textContent = this.errorMessage;
      this.errorState.removeAttribute('hidden');
    }
  }

  #renderPlaceholders() {
    if (!this.grid) return;
    const fragment = document.createDocumentFragment();
    for (let index = 0; index < this.placeholderCount; index += 1) {
      const placeholder = document.createElement('article');
      placeholder.className = 'wishlist-card wishlist-card--placeholder';
      placeholder.innerHTML = `
        <div class="wishlist-card__media">
          <div class="wishlist-card__media-link">
            <div class="wishlist-card__placeholder" aria-hidden="true"></div>
          </div>
        </div>
        <div class="wishlist-card__content">
          <div class="wishlist-card__info">
            <span class="wishlist-card__title placeholder"></span>
            <span class="wishlist-card__price placeholder"></span>
          </div>
        </div>
      `;
      fragment.appendChild(placeholder);
    }
    this.grid.replaceChildren(fragment);
    this.grid.removeAttribute('hidden');
    this.emptyState?.setAttribute('hidden', '');
    this.loadingState?.setAttribute('hidden', '');
    this.errorState?.setAttribute('hidden', '');
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPageElement);
}
